import { sumDecimalStrings } from '@/lib/decimal';
import type { SalesTargetLine, SalesTargetsView } from '@/lib/types';
import {
  COUNT_NUMBER_FORMAT,
  MONEY_NUMBER_FORMAT,
  exportRowsToXlsx,
  type XlsxExportColumn,
} from '@/lib/xlsx-export';

/**
 * Sales targets → one .xlsx sheet (owner 2026-10-28), written client-side with
 * the shared `exportRowsToXlsx` helper (the drill-down's "Export to Excel").
 *
 * Layout, top to bottom, all on one sheet with one column set:
 *   1. Summary: the cycle totals (one row).
 *   2. Regional team targets, one row per region (USD).
 *   3. Pools, one row each.
 *   4. Project lines grouped by product: a group heading row, the lines, and
 *      a subtotal row per group.
 *   5. The grand total row = `view.totals` exactly as the server sent it.
 *
 * Rules:
 *   - Every money cell is USD, written as a number cell (display-only, the
 *     conversion lives in `lib/xlsx-export`). `null` stays an empty cell.
 *   - Group subtotals are sums of decimal strings (`sumDecimalStrings`, BigInt).
 *     A pool member's own planned / target revenue is left out of its group's
 *     subtotal, because the pool row carries that plan (no double count);
 *     its achieved is counted.
 *   - All lines go in, whatever the screen shows.
 */

type Cell = string | number | null;

interface ExportRow {
  section: string;
  name: string;
  region: string | null;
  intake: string | null;
  institutionPlanned: Cell;
  institutionTarget: Cell;
  partnerPlanned: Cell;
  partnerTarget: Cell;
  travelPercent: Cell;
  months: Cell;
  unitPriceUsd: Cell;
  plannedRevenueUsd: Cell;
  targetRevenueUsd: Cell;
  plannedTravelRevenueUsd: Cell;
  targetTravelRevenueUsd: Cell;
  achievedUsd: Cell;
  gapUsd: Cell;
  achievedPct: Cell;
  expectedToDateUsd: Cell;
  paceGapUsd: Cell;
  institutionsSold: Cell;
  partnersSold: Cell;
  pool: string | null;
  notes: string | null;
}

const COUNT_FORMAT = 'General';
const PERCENT_FORMAT = '0.0';

function blankRow(section: string, name: string): ExportRow {
  return {
    section,
    name,
    region: null,
    intake: null,
    institutionPlanned: null,
    institutionTarget: null,
    partnerPlanned: null,
    partnerTarget: null,
    travelPercent: null,
    months: null,
    unitPriceUsd: null,
    plannedRevenueUsd: null,
    targetRevenueUsd: null,
    plannedTravelRevenueUsd: null,
    targetTravelRevenueUsd: null,
    achievedUsd: null,
    gapUsd: null,
    achievedPct: null,
    expectedToDateUsd: null,
    paceGapUsd: null,
    institutionsSold: null,
    partnersSold: null,
    pool: null,
    notes: null,
  };
}

/** Numbers-as-strings or numbers from the wire; `undefined` (older API) is empty. */
function cell(value: string | number | null | undefined): Cell {
  return value === undefined ? null : value;
}

/** The product a line is grouped under; lines with no product share one group. */
export function salesTargetGroupName(line: SalesTargetLine): string {
  const name = line.productName?.trim();
  return name ? name : 'Other';
}

/** Lines grouped by product, in the order the server sent them. */
export function groupSalesTargetLines(lines: readonly SalesTargetLine[]): { name: string; lines: SalesTargetLine[] }[] {
  const groups = new Map<string, SalesTargetLine[]>();
  for (const line of lines) {
    const key = salesTargetGroupName(line);
    const bucket = groups.get(key);
    if (bucket) bucket.push(line);
    else groups.set(key, [line]);
  }
  return Array.from(groups, ([name, groupLines]) => ({ name, lines: groupLines }));
}

function lineRow(line: SalesTargetLine, poolName: string | null, section: string): ExportRow {
  return {
    ...blankRow(section, line.projectName),
    region: line.regions.length > 0 ? line.regions.join(', ') : null,
    intake: line.intake,
    institutionPlanned: cell(line.institutionPlanned),
    institutionTarget: cell(line.institutionTarget),
    partnerPlanned: cell(line.partnerPlanned),
    partnerTarget: cell(line.partnerTarget),
    travelPercent: cell(line.travelPercent),
    months: cell(line.months),
    unitPriceUsd: cell(line.unitPriceUsd),
    plannedRevenueUsd: cell(line.plannedRevenueUsd),
    targetRevenueUsd: cell(line.targetRevenueUsd),
    plannedTravelRevenueUsd: cell(line.plannedTravelRevenueUsd),
    targetTravelRevenueUsd: cell(line.targetTravelRevenueUsd),
    achievedUsd: cell(line.achievedUsd),
    gapUsd: cell(line.gapUsd),
    achievedPct: cell(line.achievedPct),
    paceGapUsd: cell(line.paceGapUsd),
    institutionsSold: cell(line.institutionsSold),
    partnersSold: cell(line.partnersSold),
    pool: poolName,
    notes: line.notes,
  };
}

function subtotalRow(name: string, lines: readonly SalesTargetLine[]): ExportRow {
  const planned = lines.filter((line) => line.poolId === null);
  return {
    ...blankRow(name, `Subtotal · ${name}`),
    plannedRevenueUsd: sumDecimalStrings(planned.map((line) => line.plannedRevenueUsd)),
    targetRevenueUsd: sumDecimalStrings(planned.map((line) => line.targetRevenueUsd)),
    achievedUsd: sumDecimalStrings(lines.map((line) => line.achievedUsd)),
    gapUsd: sumDecimalStrings(planned.map((line) => line.gapUsd)),
  };
}

function buildRows(view: SalesTargetsView): ExportRow[] {
  const rows: ExportRow[] = [];
  const t = view.totals;

  rows.push({
    ...blankRow('Summary', `Cycle ${view.cycle.name}`),
    plannedRevenueUsd: cell(t.plannedRevenueUsd),
    targetRevenueUsd: cell(t.targetRevenueUsd),
    achievedUsd: cell(t.achievedUsd),
    gapUsd: cell(t.gapUsd),
    achievedPct: cell(t.achievedPct),
    expectedToDateUsd: cell(t.expectedToDateUsd),
    paceGapUsd: cell(t.paceGapUsd),
  });

  for (const region of view.regions) {
    rows.push({
      ...blankRow('Regional team target', region.label),
      region: region.billingRegions.length > 0 ? region.billingRegions.join(', ') : null,
      targetRevenueUsd: cell(region.targetUsd),
      achievedUsd: cell(region.achievedUsd),
      gapUsd: cell(region.gapUsd),
      achievedPct: cell(region.achievedPct),
      expectedToDateUsd: cell(region.expectedToDateUsd),
    });
  }

  const poolNames = new Map<number, string>();
  for (const pool of view.pools) {
    poolNames.set(pool.id, pool.name);
    rows.push({
      ...blankRow('Pool', pool.name),
      institutionPlanned: cell(pool.plannedCount),
      institutionTarget: cell(pool.targetCount),
      unitPriceUsd: cell(pool.unitPriceUsd),
      plannedRevenueUsd: cell(pool.plannedRevenueUsd),
      targetRevenueUsd: cell(pool.targetRevenueUsd),
      achievedUsd: cell(pool.achievedUsd),
      gapUsd: cell(pool.gapUsd),
      achievedPct: cell(pool.achievedPct),
      paceGapUsd: cell(pool.paceGapUsd),
    });
  }

  for (const group of groupSalesTargetLines(view.lines)) {
    rows.push(blankRow(group.name, group.name));
    for (const line of group.lines) {
      rows.push(lineRow(line, line.poolId !== null ? (poolNames.get(line.poolId) ?? null) : null, group.name));
    }
    rows.push(subtotalRow(group.name, group.lines));
  }

  return rows;
}

function column(
  key: keyof ExportRow,
  label: string,
  numFmt?: string,
): XlsxExportColumn<ExportRow> {
  return {
    key,
    label,
    value: (row) => row[key],
    ...(numFmt !== undefined ? { numFmt } : {}),
  };
}

const COLUMNS: readonly XlsxExportColumn<ExportRow>[] = [
  column('section', 'Group'),
  column('name', 'Project'),
  column('region', 'Region'),
  column('intake', 'Intake'),
  column('institutionPlanned', 'Planned capacity – Institution', COUNT_FORMAT),
  column('partnerPlanned', 'Planned capacity – Partner', COUNT_FORMAT),
  column('institutionTarget', 'Target – Institution', COUNT_FORMAT),
  column('partnerTarget', 'Target – Partner', COUNT_FORMAT),
  column('travelPercent', 'Travel %', PERCENT_FORMAT),
  column('months', 'Months', COUNT_FORMAT),
  column('unitPriceUsd', 'Unit price (USD)', MONEY_NUMBER_FORMAT),
  column('plannedRevenueUsd', 'Planned revenue (USD)', MONEY_NUMBER_FORMAT),
  column('targetRevenueUsd', 'Target revenue (USD)', MONEY_NUMBER_FORMAT),
  column('plannedTravelRevenueUsd', 'Planned travel (USD)', MONEY_NUMBER_FORMAT),
  column('targetTravelRevenueUsd', 'Target travel (USD)', MONEY_NUMBER_FORMAT),
  column('achievedUsd', 'Achieved (USD)', MONEY_NUMBER_FORMAT),
  column('gapUsd', 'Behind (USD)', MONEY_NUMBER_FORMAT),
  column('achievedPct', '% achieved', PERCENT_FORMAT),
  column('expectedToDateUsd', 'Expected to date (USD)', MONEY_NUMBER_FORMAT),
  column('paceGapUsd', 'Pace gap (USD)', MONEY_NUMBER_FORMAT),
  column('institutionsSold', 'Institutions sold', COUNT_NUMBER_FORMAT),
  column('partnersSold', 'Partners sold', COUNT_NUMBER_FORMAT),
  column('pool', 'Pool'),
  column('notes', 'Notes'),
];

/** Build the workbook for one cycle and trigger the download. Rejects if SheetJS fails to load. */
export async function exportSalesTargetsXlsx(view: SalesTargetsView): Promise<void> {
  const t = view.totals;
  const totals: Record<string, string> = { section: 'Grand total' };
  const add = (key: keyof ExportRow, value: string | null | undefined) => {
    if (value !== null && value !== undefined) totals[key] = value;
  };
  add('plannedRevenueUsd', t.plannedRevenueUsd);
  add('targetRevenueUsd', t.targetRevenueUsd);
  add('achievedUsd', t.achievedUsd);
  add('gapUsd', t.gapUsd);
  add('achievedPct', t.achievedPct);
  add('expectedToDateUsd', t.expectedToDateUsd);
  add('paceGapUsd', t.paceGapUsd);

  const rates =
    view.translation !== null
      ? `Converted to USD at ${view.translation.rate} on ${view.translation.date}`
      : 'Amounts in USD';

  await exportRowsToXlsx({
    fileName: `seed-emerge-sales-targets-${view.cycle.name.replace(/\s+/g, '')}-${view.asOf}`,
    sheetName: `Sales targets ${view.cycle.name}`,
    title: [
      `SEED EMERGE · Sales targets · ${view.cycle.name}`,
      `As of ${view.asOf}`,
      rates,
      ...(view.settings.priceTierName ? [`Card rate: ${view.settings.priceTierName} (USD)`] : []),
    ],
    columns: COLUMNS,
    rows: buildRows(view),
    totals,
  });
}
